import { FocusMonitor } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxControlSize, HxControlVariant } from '../../base/types';
import { StopPropagationDirective } from '../../directives';
import { HxChipAvatar } from './chip-avatar';
import { HxChipRemove } from './chip-remove';
import { MAT_CHIP } from './chip-tokens';
import { HxChipTrailingIcon } from './chip-trailing-icon';

export type ChipVariant = Extract<HxControlVariant, 'fill' | 'soft' | 'outline'>;
export type ChipSize = Extract<HxControlSize, 'sm' | 'md' | 'lg'>;
export type ChipType = 'none' | 'all' | 'standard' | 'other';

/**
 * Chip component for compact, interactive elements representing input, attributes, or actions.
 *
 * Features:
 *
 * - 3 visual variants: fill (solid), soft (subtle), outline (border-only)
 * - 2 sizes: medium (32px), small (24px)
 * - Selectable with keyboard support (Space/Enter)
 * - Removable with Delete key
 * - Content projection: avatar, trailing icon, remove button
 * - Token-driven theming via CSS custom properties
 * - WCAG 2.1 AA compliant
 *
 * @example
 *   ```html
 *   <hx-chip
 *     label="Filter"
 *     variant="fill"
 *     [(selected)]="isSelected"
 *     (removed)="onRemove()">
 *     <svg hxChipAvatar>...</svg>
 *     <svg hxChipRemove>...</svg>
 *   </hx-chip>
 *   ```;
 *
 * @see HxChipGroup for managing collections with form integration
 * @see HxChipAvatar for leading icons/images
 * @see HxChipTrailingIcon for trailing icons
 * @see HxChipRemove for remove button icons
 */
@Component({
  selector: 'hx-chip',
  standalone: true,
  imports: [StopPropagationDirective],
  templateUrl: './chip.html',
  styleUrls: ['./chip.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.role]': '"option"',
    '[attr.tabindex]': '_tabindex()',
    '[attr.aria-disabled]': '_ariaDisabled()',
    '[attr.aria-readonly]': '_ariaReadonly()',
    '[attr.aria-label]': '_ariaLabel()',
    '[class]': '_hostClass()',
  },
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: MAT_CHIP, useExisting: HxChip }],
})
export class HxChip extends HxBaseControl implements OnDestroy {
  // Injected dependencies
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly _elementRef = inject(ElementRef);

  // Inputs
  /** Visual variant: fill (solid), soft (subtle), outline (border). Default: 'fill' */
  readonly variant = input<ChipVariant>('fill');

  /** Size: md (32px height), sm (24px height). Default: 'md' */
  readonly size = input<ChipSize>('md');

  /** @deprecated Internal use only. Will be removed in future version. */
  readonly startItem = input<boolean>(true);

  /** @deprecated Internal use only. Will be removed in future version. */
  readonly endItem = input<boolean>(true);

  /** When remove button is functional: 'always' or only when 'selected'. Default: 'always' */
  readonly removableWhen = input<'always' | 'selected'>('always');

  /** Special chip type: 'none' deselects all; 'all' selects all; default 'standard'. */
  readonly type = input<ChipType>('standard');

  // Models (two-way binding)
  /** Two-way binding for selected/active state. */
  readonly selected = model<boolean>(false);

  // Outputs
  /** Emits when selection state changes (click, Space, or Enter key). */
  readonly selectedChange = output<boolean>();

  /** Emits when chip is removed (via remove button or Delete key). */
  readonly removed = output<void>();

  // Queries
  /** Content projection query for avatar directive */
  readonly avatar = contentChild(HxChipAvatar);

  /** Content projection query for trailing icon directive */
  readonly trailingIcon = contentChild(HxChipTrailingIcon);

  /** Content projection query for remove button directive */
  readonly removeButton = contentChild(HxChipRemove);

  // Computed signals
  /** Whether the chip is in active/selected state */
  readonly isActive = computed(() => !!this.selected());

  /** Computed flag for remove button visibility based on removableWhen condition */
  readonly _showRemoveButton = computed(() => {
    const hasRemoveContent = !!this.removeButton();
    const removable = this.removableWhen();
    return hasRemoveContent && (removable === 'always' || (removable === 'selected' && this.isActive()));
  });

  /** Computed tabindex based on disabled state and label presence */
  protected readonly _tabindex = computed(() => (this.disabledState() || !this.label() ? -1 : 0));

  /** Computed aria-disabled attribute value */
  protected readonly _ariaDisabled = computed(() => (this.disabledState() ? 'true' : null));

  /** Computed aria-readonly attribute value */
  protected readonly _ariaReadonly = computed(() => (this.readonly() ? 'true' : null));

  /** Computed aria-label with fallback for empty label edge case */
  protected readonly _ariaLabel = computed(() => this.label() || 'Chip');

  /** Computed CSS classes based on variant, size, and state */
  protected readonly _hostClass = computed(() => {
    const classes: string[] = ['hx-chip']; // Always apply base class
    const v = this.variant();
    const s = this.size();
    const t = this.type();
    if (v === 'soft') {
      classes.push('hx-chip-soft');
    }
    if (v === 'outline') {
      classes.push('hx-chip-outline');
    }
    if (s === 'sm') {
      classes.push('hx-chip-sm');
    }
    if (s === 'lg') {
      classes.push('hx-chip-lg');
    }
    if (this.isActive()) {
      classes.push('hx-chip-active');
    }
    if (this.readonly()) {
      classes.push('hx-chip-readonly');
    }
    if (t === 'none') {
      classes.push('hx-chip-none');
    } else if (t === 'all') {
      classes.push('hx-chip-all');
    }
    return classes.join(' ');
  });

  /** Computed pointer-events style based on disabled state */
  protected readonly _pointerEvents = computed(() => (this.disabledState() ? 'none' : null));

  // Lifecycle hooks
  ngOnDestroy(): void {
    // Cleanup focus monitor to prevent memory leaks
  }

  // Public methods (API)
  /** Removes the chip if removableWhen conditions are met. Public API for programmatic removal. */
  remove(): void {
    const canRemove = this.removableWhen() === 'always' || (this.removableWhen() === 'selected' && this.isActive());
    if (canRemove) {
      this.removed.emit();
    }
  }

  // Event handlers (protected)
  /** Handles click on chip (toggles selection). Blocked when disabled or readonly. */
  @HostListener('click')
  protected onClick(): void {
    if (this.disabledState() || this.readonly()) {
      return;
    }
    this.handleSelectionToggle();
  }

  /** Handles keyboard navigation on chip. Supports Space/Enter for selection toggle and Delete for removal. */
  @HostListener('keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (this.disabledState()) {
      return;
    }

    if (event.key === ' ' || event.key === 'Enter') {
      if (this.readonly()) {
        return;
      }
      event.preventDefault();
      this._elementRef.nativeElement.click();
    } else if (event.key === 'Delete') {
      event.preventDefault();
      this.remove();
    }
  }

  // Private methods (internal logic)
  /** Toggles the selected state and emits the change event. */
  private handleSelectionToggle(): void {
    const nextState = !this.selected();
    this.selected.set(nextState);
    this.selectedChange.emit(nextState);
  }
}
